import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, FileText, Stethoscope, Eye, ClipboardCheck, GraduationCap,
  ChevronDown, ChevronUp, Plus, Trash2, AlertCircle
} from 'lucide-react';

const SCREENING_FIELDS = [
  { key: 'hearing', label: 'Hearing Screening', icon: Stethoscope },
  { key: 'vision', label: 'Vision Screening', icon: Eye },
  { key: 'language_survey', label: 'Home Language Survey', icon: FileText },
];

const EVAL_TYPES = ['Initial Evaluation', 'Reevaluation', 'Independent Educational Evaluation', 'Other'];
const SCREENING_RESULTS = ['Pass', 'Fail', 'Refer', 'N/A'];

function mergeContent(existing) {
  const defaults = {
    evaluation_type: '', reason_for_referral: '',
    pre_referral_interventions: { has_interventions: false, source: 'manual', interventions: [] },
    previous_eligibility: { was_eligible: null, category: '', date: '', details: '' },
    current_eligibility_date: '',
    screening: {
      hearing: { date: '', result: '', notes: '' },
      vision: { date: '', result: '', notes: '' },
      language_survey: { date: '', result: '', notes: '' },
    },
    date_of_review: '', notes: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  const merged = JSON.parse(JSON.stringify(defaults));
  Object.keys(existing).forEach(k => {
    if (k === 'screening' && typeof existing[k] === 'object') {
      SCREENING_FIELDS.forEach(f => {
        if (existing.screening[f.key]) merged.screening[f.key] = { ...merged.screening[f.key], ...existing.screening[f.key] };
      });
    } else if (k === 'previous_eligibility' && typeof existing[k] === 'object') {
      merged.previous_eligibility = { ...merged.previous_eligibility, ...existing[k] };
    } else if (k === 'pre_referral_interventions' && typeof existing[k] === 'object') {
      merged.pre_referral_interventions = { ...merged.pre_referral_interventions, ...existing[k] };
    } else if (existing[k] !== undefined) {
      merged[k] = existing[k];
    }
  });
  return merged;
}

export default function METCoversheetEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) { ref = ref[path[i]]; }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  const handleSave = () => onSave(content);
  const interventions = content.pre_referral_interventions?.interventions || [];

  const addIntervention = () => {
    update(['pre_referral_interventions', 'interventions'], [
      ...interventions, { id: `int_${Date.now()}`, type: '', description: '', duration: '', outcome: '' },
    ]);
  };
  const removeIntervention = (idx) => {
    update(['pre_referral_interventions', 'interventions'], interventions.filter((_, i) => i !== idx));
  };
  const updateIntervention = (idx, field, value) => {
    const updated = interventions.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    update(['pre_referral_interventions', 'interventions'], updated);
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="met-coversheet-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="coversheet-title">MET Coversheet</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="coversheet-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Evaluation Type */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="eval-type-section">
        <label className="text-sm font-medium text-[#1C1C1C] mb-2 block">Evaluation Type</label>
        <Select value={content.evaluation_type || ''} onValueChange={v => update(['evaluation_type'], v)}>
          <SelectTrigger className="rounded-xl text-sm h-9 max-w-sm" data-testid="eval-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>{EVAL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Reason for Referral */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-reason-section">
        <label className="text-sm font-medium text-[#1C1C1C] mb-2 block">Reason for Referral</label>
        <Textarea value={content.reason_for_referral || ''} onChange={e => update(['reason_for_referral'], e.target.value)}
          placeholder="Describe the reason for this evaluation referral..." rows={4} className="rounded-xl border-[#E5E5E5] text-sm"
          data-testid="referral-reason-input" />
      </div>

      {/* Pre-referral Interventions */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="pre-referral-section">
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-[#5A8A5A]" />
            <h3 className="font-medium text-sm text-[#1C1C1C]">Pre-referral Interventions</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#595959]">Has interventions?</span>
            <button className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${content.pre_referral_interventions?.has_interventions ? 'bg-[#5A8A5A] text-white' : 'bg-[#E5E5E5] text-[#595959] hover:bg-[#d9d9d9]'}`}
              onClick={() => update(['pre_referral_interventions', 'has_interventions'], !content.pre_referral_interventions?.has_interventions)}
              data-testid="pre-referral-toggle">
              {content.pre_referral_interventions?.has_interventions ? 'Yes' : 'No'}
            </button>
          </div>
        </div>

        {content.pre_referral_interventions?.has_interventions && (
          <div className="p-5 space-y-4">
            {/* Source note — future integration placeholder */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">Source: <strong>{content.pre_referral_interventions.source === 'manual' ? 'Manual Entry' : 'Frame Interventions'}</strong>. Future: pull from Frame Interventions module.</p>
            </div>

            {interventions.length === 0 ? (
              <p className="text-sm text-[#595959] text-center py-4">No interventions added yet.</p>
            ) : (
              <div className="space-y-3">
                {interventions.map((item, idx) => (
                  <div key={item.id || idx} className="border border-[#E5E5E5] rounded-xl p-4 relative group" data-testid={`intervention-${idx}`}>
                    <button onClick={() => removeIntervention(idx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition">
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Intervention Type</label>
                        <Input value={item.type} onChange={e => updateIntervention(idx, 'type', e.target.value)} placeholder="e.g., Tier 2 Reading" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Duration</label>
                        <Input value={item.duration} onChange={e => updateIntervention(idx, 'duration', e.target.value)} placeholder="e.g., 8 months" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Description</label>
                      <Textarea value={item.description} onChange={e => updateIntervention(idx, 'description', e.target.value)} placeholder="Describe the intervention..." rows={2} className="rounded-xl border-[#E5E5E5] text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Outcome</label>
                      <Textarea value={item.outcome} onChange={e => updateIntervention(idx, 'outcome', e.target.value)} placeholder="Describe the outcome/progress..." rows={2} className="rounded-xl border-[#E5E5E5] text-sm" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={addIntervention} data-testid="add-intervention-btn">
              <Plus className="h-3 w-3 mr-1" />Add Intervention
            </Button>
          </div>
        )}
      </div>

      {/* Previous Eligibility */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="prev-eligibility-section">
        <h3 className="text-sm font-medium text-[#1C1C1C] mb-3">Previous Eligibility</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-[#595959] mb-1 block">Previously eligible?</label>
            <Select value={content.previous_eligibility?.was_eligible === true ? 'yes' : content.previous_eligibility?.was_eligible === false ? 'no' : ''} onValueChange={v => update(['previous_eligibility', 'was_eligible'], v === 'yes')}>
              <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#595959] mb-1 block">Category</label>
            <Input value={content.previous_eligibility?.category || ''} onChange={e => update(['previous_eligibility', 'category'], e.target.value)} placeholder="Disability category" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#595959] mb-1 block">Date</label>
            <Input type="date" value={content.previous_eligibility?.date || ''} onChange={e => update(['previous_eligibility', 'date'], e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
          </div>
        </div>
      </div>

      {/* Screening */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="screening-section">
        <h3 className="text-sm font-medium text-[#1C1C1C] mb-4">Screenings</h3>
        <div className="space-y-4">
          {SCREENING_FIELDS.map(field => {
            const data = content.screening?.[field.key] || {};
            return (
              <div key={field.key} className="border border-[#E5E5E5] rounded-xl p-4" data-testid={`screening-${field.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <field.icon className="h-4 w-4 text-[#5A8A5A]" />
                  <span className="text-sm font-medium text-[#1C1C1C]">{field.label}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#595959] mb-1 block">Date</label>
                    <Input type="date" value={data.date || ''} onChange={e => update(['screening', field.key, 'date'], e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#595959] mb-1 block">Result</label>
                    <Select value={data.result || ''} onValueChange={v => update(['screening', field.key, 'result'], v)}>
                      <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue placeholder="Result" /></SelectTrigger>
                      <SelectContent>{SCREENING_RESULTS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#595959] mb-1 block">Notes</label>
                    <Input value={data.notes || ''} onChange={e => update(['screening', field.key, 'notes'], e.target.value)} placeholder="Optional notes" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Date and Notes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
          <label className="text-sm font-medium text-[#1C1C1C] mb-2 block">Date of Review</label>
          <Input type="date" value={content.date_of_review || ''} onChange={e => update(['date_of_review'], e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="date-of-review" />
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
          <label className="text-sm font-medium text-[#1C1C1C] mb-2 block">Additional Notes</label>
          <Textarea value={content.notes || ''} onChange={e => update(['notes'], e.target.value)} placeholder="Any additional coversheet notes..." rows={2} className="rounded-xl border-[#E5E5E5] text-sm" data-testid="coversheet-notes" />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="coversheet-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Coversheet'}
        </Button>
      </div>
    </div>
  );
}
